import { Fragment, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import SalesLayout from '../../components/SalesLayout';
import axiosInstance from '../../utils/axiosInstance';

const formatCurrency = (value) => {
    if (!Number.isFinite(value)) {
        return 'Rs 0';
    }

    const rounded = Math.round(value);
    const formatter = new Intl.NumberFormat('en-US');

    return `Rs ${formatter.format(rounded)}`;
};

const formatCount = (value) => {
    if (!Number.isFinite(value)) {
        return '0';
    }

    return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);
};

const buildMotivationMessage = (row) => {
    const targetAmount = row.targetAmount || 0;
    const registeredAmount = row.registeredAmount || 0;
    const balanceAmount = Math.max(0, row.balanceAmount || targetAmount - registeredAmount);
    const progress = targetAmount > 0 ? (registeredAmount / targetAmount) * 100 : 0;
    const roundedProgress = Math.round(progress);

    if (roundedProgress >= 100) {
        return 'Phenomenal work! You have exceeded this month\'s target—keep the conversations warm for next month\'s pipeline.';
    }

    if (roundedProgress >= 75) {
        return `Only ${formatCurrency(balanceAmount)} left to close. Double down on high-intent leads to push past the finish line.`;
    }

    if (roundedProgress >= 50) {
        return `Great momentum so far. Prioritise the ${formatCount(row.planToRegisterCount || 0)} planned sign-ups to surge ahead.`;
    }

    return `Kick-start the month by reconnecting with ${formatCount(row.followupCount || 0)} follow-ups and aim for ${formatCurrency(targetAmount * 0.25)} in wins this week.`;
};

const formatStatus = (status) => {
    if (!status) {
        return 'Status unknown';
    }

    return status
        .split(' ')
        .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part))
        .join(' ');
};

const formatDate = (value) => {
    if (!value) {
        return 'Not set';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(parsed);
};

const deriveStatusKey = (entry) => (entry.statusNormalized ?? entry.status ?? 'unknown').toLowerCase();

export default function Target({ pageTitle, section }) {
    const [counselor, setCounselor] = useState(null);
    const [targets, setTargets] = useState([]);
    const [targetStats, setTargetStats] = useState({
        achievementPercent: 0,
        totalTargetAmount: 0,
        totalRegisteredAmount: 0,
        remainingAmount: 0,
    });
    const [incomeEntries, setIncomeEntries] = useState([]);
    const [monthlyIncomeSummary, setMonthlyIncomeSummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const [showTargetModal, setShowTargetModal] = useState(false);
    const [targetModalMode, setTargetModalMode] = useState('create'); // 'create' | 'edit'
    const [targetForm, setTargetForm] = useState({ monthKey: '', targetAmount: '' });
    const [savingTarget, setSavingTarget] = useState(false);
    const [targetFormError, setTargetFormError] = useState(null);
    const [deletingMonthKey, setDeletingMonthKey] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axiosInstance.get('/admin/sales-targets');
                if (!response?.data || cancelled) return;

                const data = response.data;
                setCounselor(data.counselor ?? null);
                setTargets(Array.isArray(data.targets) ? data.targets : []);
                setTargetStats(data.targetStats ?? {
                    achievementPercent: 0,
                    totalTargetAmount: 0,
                    totalRegisteredAmount: 0,
                    remainingAmount: 0,
                });
                setIncomeEntries(Array.isArray(data.incomeEntries) ? data.incomeEntries : []);
                setMonthlyIncomeSummary(Array.isArray(data.monthlyIncomeSummary) ? data.monthlyIncomeSummary : []);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to load sales targets:', err);
                if (!cancelled) {
                    setError('Unable to load counselor targets right now.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, [reloadToken]);

    const counselorName = counselor?.name ?? 'Sales Counselor';
    const counselorRole = counselor?.position ?? counselor?.type ?? null;
    const counselorCode = counselor?.employeeNumber ?? null;
    const targetRows = targets ?? [];
    const achievementPercent = Math.max(0, Math.min(targetStats?.achievementPercent ?? 0, 100));
    const confirmedEntries = incomeEntries ?? [];
    const incomeSummary = monthlyIncomeSummary ?? [];
    const incomeSummaryMap = new Map();
    incomeSummary.forEach((month) => {
        incomeSummaryMap.set(month.monthKey, month);
    });

    const [statusFilter, setStatusFilter] = useState('all');
    const [showAllTargets, setShowAllTargets] = useState(false);

    const today = new Date();
    const currentFYStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1; // April is month index 3
    const currentFYEndYear = currentFYStartYear + 1;

    const financialYearLabel = `${currentFYStartYear} - April to ${currentFYEndYear} - March`;

    const isInCurrentFinancialYear = (monthKey) => {
        // Expecting format YYYY-MM or YYYY-M; fallback attempts for robustness
        const parts = monthKey.split('-');
        if (parts.length < 2) return false;
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10); // 1-12
        if (!Number.isFinite(year) || !Number.isFinite(month)) return false;

        // Months Apr(4) to Dec(12) belong to start year; Jan(1) to Mar(3) belong to end year
        if (month >= 4 && month <= 12) {
            return year === currentFYStartYear;
        }
        if (month >= 1 && month <= 3) {
            return year === currentFYEndYear;
        }
        return false;
    };

    const targetRowsToShow = useMemo(() => {
        if (showAllTargets) return targetRows;
        return targetRows.filter((row) => isInCurrentFinancialYear(row.monthKey));
    }, [showAllTargets, targetRows]);

    const statusOptions = useMemo(() => {
        const registry = new Map();
        registry.set('all', 'All');

        confirmedEntries.forEach((entry) => {
            const key = deriveStatusKey(entry);

            if (!registry.has(key)) {
                registry.set(key, formatStatus(entry.status ?? key));
            }
        });

        return Array.from(registry.entries()).map(([key, label]) => ({ key, label }));
    }, [confirmedEntries]);

    const activeStatusLabel = statusOptions.find((option) => option.key === statusFilter)?.label ?? 'All';

    const filteredEntries = useMemo(() => {
        if (statusFilter === 'all') {
            return confirmedEntries;
        }

        return confirmedEntries.filter((entry) => deriveStatusKey(entry) === statusFilter);
    }, [confirmedEntries, statusFilter]);

    const incomeSummaryToShow = useMemo(() => {
        if (showAllTargets) return incomeSummary;
        return incomeSummary.filter((m) => isInCurrentFinancialYear(m.monthKey));
    }, [showAllTargets, incomeSummary]);

    const overallTotals = useMemo(() => {
        const totals = incomeSummaryToShow.reduce(
            (acc, month) => {
                acc.registrationIncome += month.registrationIncome ?? 0;
                acc.registrationCount += month.registrationCount ?? 0;
                acc.planToRegisterCount += month.planToRegisterCount ?? 0;
                acc.planToRegisterAmount += month.planToRegisterAmount ?? 0;
                acc.followupCount += month.followupCount ?? 0;
                acc.willNotRegisterAmount += month.willNotRegisterAmount ?? 0;
                return acc;
            },
            {
                registrationIncome: 0,
                registrationCount: 0,
                planToRegisterCount: 0,
                planToRegisterAmount: 0,
                followupCount: 0,
                willNotRegisterAmount: 0,
            },
        );

        const targetAmount = targetStats?.totalTargetAmount ?? 0;
        const pendingToAchieve = Math.max(0, targetAmount - totals.registrationIncome);

        return {
            ...totals,
            targetAmount,
            pendingToAchieve,
        };
    }, [incomeSummaryToShow, targetStats]);

    const filteredTotals = useMemo(() => {
        const entryCount = filteredEntries.length;
        let totalAmount = 0;
        let registrationIncome = 0;
        let registrationCount = 0;

        filteredEntries.forEach((entry) => {
            const amount = entry.confirmedAmount ?? 0;
            totalAmount += amount;

            if (deriveStatusKey(entry) === 'registered') {
                registrationIncome += amount;
                registrationCount += 1;
            }
        });

        return {
            entryCount,
            totalAmount,
            registrationIncome,
            registrationCount,
        };
    }, [filteredEntries]);

    const overallRegistrationIncome = overallTotals.registrationIncome ?? 0;
    const overallPendingToAchieve = overallTotals.pendingToAchieve ?? 0;
    const overallRegistrationCount = overallTotals.registrationCount ?? 0;
    const overallWillNotRegisterAmount = overallTotals.willNotRegisterAmount ?? 0;
    const overallPlanToRegisterAmount = overallTotals.planToRegisterAmount ?? 0;
    const overallPlanToRegisterCount = overallTotals.planToRegisterCount ?? 0;

    const filteredRegistrationIncome = filteredTotals.registrationIncome ?? 0;
    const filteredEntryCount = filteredTotals.entryCount ?? 0;
    const filteredTotalAmount = filteredTotals.totalAmount ?? 0;
    const filteredRegisteredCount = filteredTotals.registrationCount ?? 0;

    const hasAnyEntries = confirmedEntries.length > 0;
    const hasFilteredEntries = filteredEntries.length > 0;

    const openCreateTargetModal = () => {
        setTargetModalMode('create');
        setTargetForm({ monthKey: '', targetAmount: '' });
        setTargetFormError(null);
        setShowTargetModal(true);
    };

    const openEditTargetModal = (row) => {
        if (!row) return;
        setTargetModalMode('edit');
        setTargetForm({
            monthKey: row.monthKey || '',
            targetAmount: Number.isFinite(row.targetAmount) ? String(row.targetAmount) : '',
        });
        setTargetFormError(null);
        setShowTargetModal(true);
    };

    const handleTargetFormChange = (event) => {
        const { name, value } = event.target;
        setTargetForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleTargetFormSubmit = async (event) => {
        event.preventDefault();
        setTargetFormError(null);

        const monthKey = String(targetForm.monthKey || '').trim();
        const amountRaw = Number(targetForm.targetAmount);

        if (!monthKey) {
            setTargetFormError('Please select a month for the target.');
            return;
        }

        if (!Number.isFinite(amountRaw) || amountRaw <= 0) {
            setTargetFormError('Please enter a valid target amount greater than 0.');
            return;
        }

        try {
            setSavingTarget(true);
            const response = await axiosInstance.post('/admin/sales-targets', {
                monthKey,
                targetAmount: amountRaw,
            });

            const data = response?.data || {};
            await Swal.fire({
                icon: 'success',
                title: 'Target saved',
                text: data.message || 'Counselor target has been saved successfully.',
                confirmButtonColor: '#0f766e',
            });

            setShowTargetModal(false);
            setTargetForm({ monthKey: '', targetAmount: '' });
            setTargetFormError(null);
            setReloadToken((token) => token + 1);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to save counselor target:', err);
            const message = err?.response?.data?.message || 'Failed to save counselor target.';
            setTargetFormError(message);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message,
                confirmButtonColor: '#b91c1c',
            });
        } finally {
            setSavingTarget(false);
        }
    };

    const handleDeleteTarget = async (row) => {
        if (!row?.monthKey) return;

        const result = await Swal.fire({
            title: 'Delete target?',
            text: `This will remove the target for ${row.monthYear}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            setDeletingMonthKey(row.monthKey);
            await axiosInstance.delete(`/admin/sales-targets/${encodeURIComponent(row.monthKey)}`);

            await Swal.fire({
                icon: 'success',
                title: 'Deleted',
                text: 'Counselor target has been deleted.',
                confirmButtonColor: '#0f766e',
            });

            setReloadToken((token) => token + 1);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to delete counselor target:', err);
            const message = err?.response?.data?.message || 'Failed to delete counselor target.';
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: message,
                confirmButtonColor: '#b91c1c',
            });
        } finally {
            setDeletingMonthKey(null);
        }
    };

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            <div className="space-y-6">
                <section className="rounded-2xl bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-500 p-6 text-white shadow-lg">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold">Counselor Targets</h1>
                            <p className="mt-1 text-sm text-white/80">Manage counselor performance targets</p>
                            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-white/90">
                                <span className="rounded-full bg-white/15 px-3 py-1 font-medium">{counselorName}</span>
                                {counselorRole && (
                                    <span className="rounded-full border border-white/40 bg-white/10 px-3 py-1">{counselorRole}</span>
                                )}
                                {counselorCode && (
                                    <span className="rounded-full border border-white/40 bg-white/10 px-3 py-1">ID: {counselorCode}</span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white/70"
                            onClick={openCreateTargetModal}
                            disabled={loading || savingTarget}
                        >
                            Add New Target
                        </button>
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Counselor Targets</h2>
                            <p className="mt-1 text-sm text-slate-500">Current Financial Year: {financialYearLabel}</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                    checked={showAllTargets}
                                    onChange={(e) => setShowAllTargets(e.target.checked)}
                                />
                                <span>Show all target details</span>
                            </label>
                        </div>
                    </div>
                    {targetRowsToShow.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200">
                                <thead>
                                    <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        <th className="px-4 py-3">No</th>
                                        <th className="px-4 py-3">Month/Year</th>
                                        <th className="px-4 py-3">Target Amount</th>
                                        <th className="px-4 py-3">Registered Count</th>
                                        <th className="px-4 py-3">Registered Amount</th>
                                        <th className="px-4 py-3">Balance Amount</th>
                                        <th className="px-4 py-3">Plan to Register Count</th>
                                        <th className="px-4 py-3">Plan to Register Amount</th>
                                        <th className="px-4 py-3">Followup Count</th>
                                        <th className="px-4 py-3 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                                    {targetRowsToShow.map((row) => {
                                        const progress = Math.max(
                                            0,
                                            Math.min(row.targetAmount > 0 ? (row.registeredAmount / row.targetAmount) * 100 : 0, 100),
                                        );
                                        const motivationMessage = buildMotivationMessage(row);
                                        const monthSummary = incomeSummaryMap.get(row.monthKey);
                                        const registrationIncome = monthSummary?.registrationIncome ?? 0;
                                        const registrationCount = monthSummary?.registrationCount ?? 0;
                                        const willNotRegisterAmount = monthSummary?.willNotRegisterAmount ?? 0;

                                        return (
                                            <Fragment key={row.id}>
                                                <tr className="bg-white">
                                                    <td className="px-4 py-3">
                                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                                                            {row.sequence}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 font-semibold text-slate-900">{row.monthYear}</td>
                                                    <td className="px-4 py-3 font-semibold text-emerald-600">{formatCurrency(row.targetAmount)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-sm font-semibold text-sky-600">
                                                            {formatCount(row.registeredCount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 font-semibold text-sky-600">{formatCurrency(row.registeredAmount)}</td>
                                                    <td className="px-4 py-3 font-semibold text-amber-600">{formatCurrency(row.balanceAmount)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="inline-flex items-center rounded-full bg-slate-200 px-3 py-1 text-sm font-semibold text-slate-600">
                                                            {formatCount(row.planToRegisterCount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 font-semibold text-sky-600">{formatCurrency(row.planToRegisterAmount)}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700">
                                                            {formatCount(row.followupCount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex justify-center gap-2">
                                                            <button
                                                                type="button"
                                                                className="rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-400/60"
                                                                onClick={() => openEditTargetModal(row)}
                                                                disabled={savingTarget || deletingMonthKey === row.monthKey}
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-rose-500/60"
                                                                onClick={() => handleDeleteTarget(row)}
                                                                disabled={savingTarget || deletingMonthKey === row.monthKey}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr className="bg-slate-50/60">
                                                    <td className="px-4 pb-5 pt-3" colSpan={10}>
                                                        <div className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-inner">
                                                            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                                <span>Monthly Progress</span>
                                                                <span className="text-sky-600">{progress.toFixed(1)}%</span>
                                                            </div>
                                                            <div className="mt-2 h-2 rounded-full bg-slate-200">
                                                                <div
                                                                    className="h-2 rounded-full bg-gradient-to-r from-rose-500 via-orange-500 to-emerald-500"
                                                                    style={{ width: `${progress}%` }}
                                                                />
                                                            </div>
                                                            <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                                                                <div>
                                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Registered Amount (Target)</div>
                                                                    <div className="mt-1 font-semibold text-slate-900">{formatCurrency(row.registeredAmount)}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Registration Income</div>
                                                                    <div className="mt-1 font-semibold text-emerald-600">{formatCurrency(registrationIncome)}</div>
                                                                    <div className="text-xs text-slate-500">{formatCount(registrationCount)} registrations</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Balance To Target</div>
                                                                    <div className="mt-1 font-semibold text-slate-900">{formatCurrency(Math.max(0, row.balanceAmount))}</div>
                                                                </div>
                                                                <div>
                                                                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan & Recovery</div>
                                                                    <div className="mt-1 font-semibold text-slate-900">{formatCount(row.planToRegisterCount)} planned</div>
                                                                    <div className="text-xs text-rose-500">Will not register impact: {formatCurrency(willNotRegisterAmount)}</div>
                                                                </div>
                                                            </div>
                                                            <div className="mt-4 rounded-lg bg-slate-900/90 px-4 py-3 text-sm text-white shadow-lg">
                                                                <p className="font-semibold leading-relaxed">{motivationMessage}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-8 text-center text-sm text-slate-500">No counselor targets found for your account yet.</div>
                    )}

                    <div className="border-t border-slate-200 bg-slate-50 px-6 py-5">
                        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                            <span>Target Achievement Progress</span>
                            <span className="text-sky-600">{achievementPercent.toFixed(1)}%</span>
                        </div>
                        <div className="mt-3 h-2 rounded-full bg-slate-200">
                            <div
                                className="h-2 rounded-full bg-gradient-to-r from-rose-500 via-orange-500 to-emerald-500"
                                style={{ width: `${achievementPercent}%` }}
                            />
                        </div>
                        <dl className="mt-4 grid gap-4 text-sm text-slate-600 md:grid-cols-3">
                            <div>
                                <dt className="font-semibold text-slate-500">Total Target</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCurrency(targetStats?.totalTargetAmount ?? 0)}</dd>
                            </div>
                            <div>
                                <dt className="font-semibold text-slate-500">Registered Amount</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCurrency(targetStats?.totalRegisteredAmount ?? 0)}</dd>
                            </div>
                            <div>
                                <dt className="font-semibold text-slate-500">Balance</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCurrency(targetStats?.remainingAmount ?? 0)}</dd>
                            </div>
                        </dl>
                    </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-200 px-6 py-4">
                        <h2 className="text-lg font-semibold text-slate-900">Monthly Income Summary</h2>
                        <p className="mt-1 text-sm text-slate-500">Confirmed registrations captured from student inquiries assigned to you.</p>
                    </div>
                    {incomeSummaryToShow.length > 0 ? (
                        <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
                            {incomeSummaryToShow.map((month) => (
                                <div key={month.monthKey} className="rounded-xl border border-slate-100 bg-slate-50/60 p-5 shadow-inner">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="text-base font-semibold text-slate-900">{month.label}</h3>
                                            <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                                                {formatCount(month.registrationCount)} registrations • Avg {formatCurrency(month.averagePayment)}
                                            </p>
                                        </div>
                                        <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                                            Total {formatCurrency(month.totalConfirmed)}
                                        </span>
                                    </div>
                                    <div className="mt-4 text-2xl font-bold text-emerald-600">{formatCurrency(month.registrationIncome)}</div>
                                    <p className="text-xs uppercase tracking-wide text-emerald-700">Registration income</p>
                                    <div className="mt-3 rounded-lg bg-rose-100/70 px-3 py-2 text-xs font-semibold text-rose-600">
                                        Will not register impact: {formatCurrency(month.willNotRegisterAmount)}
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
                                        <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-700">
                                            Plan to register: {formatCount(month.planToRegisterCount)} ({formatCurrency(month.planToRegisterAmount)})
                                        </span>
                                        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-700">
                                            Follow-ups: {formatCount(month.followupCount)}
                                        </span>
                                    </div>
                                    {month.statusBreakdown.length > 0 && (
                                        <div className="mt-4 space-y-2 text-sm text-slate-600">
                                            {month.statusBreakdown.map((status) => (
                                                <div key={`${month.monthKey}-${status.status ?? 'unknown'}`} className="flex items-center justify-between">
                                                    <span className="capitalize text-slate-500">{formatStatus(status.status)}</span>
                                                    <span className="text-slate-900">
                                                        {formatCount(status.count)} • {formatCurrency(status.amount)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-6 text-sm text-slate-500">No confirmed registrations have been recorded for your inquiries yet.</div>
                    )}
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-200 px-6 py-4">
                        <h2 className="text-lg font-semibold text-slate-900">Recent Confirmed Registrations</h2>
                        <p className="mt-1 text-sm text-slate-500">Detailed view of student inquiries marked as registered, including payment commitments.</p>
                    </div>
                    <div className="px-6 py-4 space-y-4">
                        <div className="flex flex-wrap gap-2">
                            {statusOptions.map((option) => {
                                const isActive = option.key === statusFilter;

                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        onClick={() => setStatusFilter(option.key)}
                                        className={`rounded-full px-3 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                                            isActive ? 'bg-sky-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                );
                            })}
                        </div>
                        <dl className="grid gap-4 text-sm text-slate-600 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Achieved Income</dt>
                                <dd className="mt-1 font-semibold text-emerald-600">{formatCurrency(overallRegistrationIncome)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending To Target</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCurrency(overallPendingToAchieve)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Registrations</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCount(overallRegistrationCount)}</dd>
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{`Active Filter (${activeStatusLabel})`}</dt>
                                <dd className="mt-1 font-semibold text-slate-900">{formatCurrency(filteredRegistrationIncome)}</dd>
                                <p className="text-xs text-slate-500">
                                    {formatCount(filteredRegisteredCount)} registrations • {formatCount(filteredEntryCount)} records • Total volume {formatCurrency(filteredTotalAmount)}
                                </p>
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan To Register Pipeline</dt>
                                <dd className="mt-1 font-semibold text-sky-600">{formatCurrency(overallPlanToRegisterAmount)}</dd>
                                <p className="text-xs text-slate-500">{formatCount(overallPlanToRegisterCount)} inquiries</p>
                            </div>
                            <div>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Follow-up Touchpoints</dt>
                                <dd className="mt-1 font-semibold text-amber-600">{formatCount(overallTotals.followupCount ?? 0)}</dd>
                                <p className="text-xs text-slate-500">Across all months</p>
                            </div>
                        </dl>
                    </div>
                    {hasAnyEntries ? (
                        hasFilteredEntries ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        <tr>
                                            <th className="px-4 py-3">Student</th>
                                            <th className="px-4 py-3">Course</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3">Confirmed Date</th>
                                            <th className="px-4 py-3 text-right">Confirmed Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                                        {filteredEntries.map((entry) => {
                                            const statusKey = deriveStatusKey(entry);
                                            const statusTone =
                                                statusKey === 'registered'
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : statusKey.includes('will')
                                                        ? 'bg-rose-100 text-rose-700'
                                                        : statusKey.includes('plan')
                                                            ? 'bg-sky-100 text-sky-700'
                                                            : statusKey.includes('follow')
                                                                ? 'bg-amber-100 text-amber-700'
                                                                : statusKey.includes('pending')
                                                                    ? 'bg-slate-300 text-slate-700'
                                                                    : 'bg-slate-200 text-slate-700';

                                            return (
                                                <tr key={entry.id} className="bg-white">
                                                    <td className="px-4 py-3 font-semibold text-slate-900">{entry.studentName ?? 'Unknown'}</td>
                                                    <td className="px-4 py-3 text-slate-600">{entry.course?.shortName ?? 'N/A'}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusTone}`}>
                                                            {formatStatus(entry.status)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-600">
                                                        <div>{formatDate(entry.confirmedDate)}</div>
                                                        {entry.registeredDate && (
                                                            <div className="text-xs text-slate-400">Actual: {formatDate(entry.registeredDate)}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrency(entry.confirmedAmount)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="px-6 pb-6 text-sm text-slate-500">No inquiries match the "{activeStatusLabel}" status yet.</div>
                        )
                    ) : (
                        <div className="p-6 text-sm text-slate-500">No registered inquiries found for your assignment just yet.</div>
                    )}
                </section>

                {showTargetModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
                        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        {targetModalMode === 'edit' ? 'Edit Monthly Target' : 'Add Monthly Target'}
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500">
                                        Set the counselor&apos;s revenue target for a specific month.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!savingTarget) {
                                            setShowTargetModal(false);
                                            setTargetFormError(null);
                                        }
                                    }}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200"
                                >
                                    <span className="sr-only">Close</span>
                                    <span className="text-lg">×</span>
                                </button>
                            </div>

                            {targetFormError && (
                                <div className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                    {targetFormError}
                                </div>
                            )}

                            <form onSubmit={handleTargetFormSubmit} className="mt-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700" htmlFor="monthKey">
                                        Month
                                    </label>
                                    <input
                                        id="monthKey"
                                        name="monthKey"
                                        type="month"
                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                                        value={targetForm.monthKey}
                                        onChange={handleTargetFormChange}
                                        disabled={savingTarget}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700" htmlFor="targetAmount">
                                        Target amount (Rs)
                                    </label>
                                    <input
                                        id="targetAmount"
                                        name="targetAmount"
                                        type="number"
                                        min="0"
                                        step="1000"
                                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                                        value={targetForm.targetAmount}
                                        onChange={handleTargetFormChange}
                                        disabled={savingTarget}
                                        required
                                    />
                                </div>
                                <div className="mt-2 flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!savingTarget) {
                                                setShowTargetModal(false);
                                                setTargetFormError(null);
                                            }
                                        }}
                                        className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                                        disabled={savingTarget}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="inline-flex items-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500/60 disabled:opacity-70"
                                        disabled={savingTarget}
                                    >
                                        {savingTarget ? 'Saving…' : targetModalMode === 'edit' ? 'Update Target' : 'Save Target'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </SalesLayout>
    );
}
